/**
 * Vaaripa — single source of truth for responsive breakpoints.
 *
 * Every breakpoint in the app (JS media hooks in utils/responsive.js and the
 * @media queries in styled-components) resolves through this file. To change a
 * device boundary, change it here once.
 *
 * NOTE: values below are preserved exactly as they existed across the codebase
 * before consolidation (no visual behaviour was changed during the merge). The
 * numbers are intentionally not all "clean" yet — normalising them is a
 * separate, opt-in step because it changes how screens break.
 *
 * Canonical device model:
 *   mobile   : <= 767px
 *   tablet   : 768px – 991px
 *   desktop  : >= 992px
 *
 * Legacy per-query values still in use (480 / 900 / 1024 / 768-as-max) are kept
 * as named tokens so existing queries keep their exact pixel value.
 */

// Numeric tokens — use these in JS (useMediaQuery, window.innerWidth checks).
export const bp = {
  mobileMax: 767, // mobile is anything at or below this
  tabletMin: 768, // tablet range lower bound
  tabletMax: 991, // tablet range upper bound
  desktopMin: 992, // desktop is anything at or above this

  // Legacy values still referenced by individual queries (value-preserving).
  sm: 480,
  md: 900,
  wide: 1024,
};

/**
 * @media condition strings for use inside styled-components template literals:
 *
 *   const Box = styled.div`
 *     padding: 24px;
 *     ${media.maxMobile} { padding: 12px; }
 *   `;
 *
 * Keys named by exact value so every migrated query keeps its original pixel
 * value byte-for-byte. Semantic aliases below map to the canonical device model.
 */
export const media = {
  // exact-value tokens (used by the migration; preserve original values)
  max480: `@media (max-width: ${bp.sm}px)`,
  max768: `@media (max-width: ${bp.tabletMin}px)`,
  max900: `@media (max-width: ${bp.md}px)`,
  max1024: `@media (max-width: ${bp.wide}px)`,
  min768: `@media (min-width: ${bp.tabletMin}px)`,
  min900: `@media (min-width: ${bp.md}px)`,
  // extra legacy per-component values kept verbatim
  max360: `@media (max-width: 360px)`,
  max600: `@media (max-width: 600px)`,
  max640: `@media (max-width: 640px)`,
  // "screen and" variants (kept verbatim)
  screenMaxMobile: `@media screen and (max-width: ${bp.mobileMax}px)`,
  screenMaxTablet: `@media screen and (max-width: ${bp.tabletMax}px)`,
  screenMinDesktop: `@media screen and (min-width: ${bp.desktopMin}px)`,
};

// Semantic aliases — prefer these for NEW / future code.
media.maxSmall = media.max480; // small phones
media.maxMobile = media.max768; // phones (legacy uses 768 as the mobile cut)
media.maxTablet = media.max1024; // up to the tablet/desktop edge (legacy 1024)
media.minTablet = media.min768; // tablet and up
media.minDesktop = media.screenMinDesktop; // desktop and up

const breakpoints = { bp, media };
export default breakpoints;
